import { BrowserRouter, HashRouter, Link, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import "./App.css";
import GIFConverter from "./routes/GifConverter";
import Watermark from "./routes/Watermark";
import ImageController from "./routes/ImageController";
import Meta from "./components/Meta";

const { guides: contentGuides, guideMap } = require("./content/guides");

const tools = [
  {
    path: "/tools/video-to-gif",
    eyebrow: "VIDEO",
    icon: "▶",
    title: "동영상 GIF 변환",
    description: "영상의 원하는 구간만 잘라 가볍고 선명한 GIF로 만드세요.",
    accent: "coral",
  },
  {
    path: "/tools/image-resizer",
    eyebrow: "IMAGE",
    icon: "↗",
    title: "이미지 리사이즈",
    description: "블로그와 SNS에 맞게 크기·포맷·품질을 한 번에 조절하세요.",
    accent: "blue",
  },
  {
    path: "/tools/watermark",
    eyebrow: "BRAND",
    icon: "◈",
    title: "워터마크 넣기",
    description: "사진에 내 이름이나 채널명을 넣고 콘텐츠를 보호하세요.",
    accent: "yellow",
  },
];

/* 이전의 짧은 가이드 데이터는 새 콘텐츠 구조로 교체되었습니다.
const guides = [
  {
    slug: "blog-image-size",
    tag: "블로그 이미지",
    title: "블로그 이미지, 몇 px로 올려야 선명할까?",
    summary: "네이버·티스토리·워드프레스에서 깨지지 않는 이미지 크기와 용량을 정리했습니다.",
    read: "5분",
  },
  {
    slug: "gif-optimization",
    tag: "GIF 만들기",
    title: "화질은 지키고 GIF 용량은 줄이는 5가지 방법",
    summary: "길이, FPS, 해상도 중 무엇부터 줄여야 하는지 실제 작업 순서로 알려드립니다.",
    read: "6분",
  },
  {
    slug: "watermark-guide",
    tag: "콘텐츠 보호",
    title: "사진을 해치지 않는 워터마크 위치와 투명도",
    summary: "도용은 막고 사진은 살리는 워터마크 디자인의 기본 원칙을 알아보세요.",
    read: "4분",
  },
];
*/

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Brand() {
  return (
    <Link className="brand" to="/" aria-label="BlogToolTool 홈">
      <img className="brand-mark" src="/brand-icon.svg" alt="" width="32" height="32" />
      <span>BlogToolTool</span>
    </Link>
  );
}

function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="site-shell">
      <header className="site-header">
        <div className="nav-wrap">
          <Brand />
          <button className="menu-button" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="메뉴 열기">
            <span /><span />
          </button>
          <nav className={open ? "main-nav open" : "main-nav"} aria-label="주 메뉴">
            <NavLink to="/tools">도구</NavLink>
            <NavLink to="/guides">활용 가이드</NavLink>
            <NavLink to="/about">소개</NavLink>
            <Link className="nav-cta" to="/tools">무료로 시작하기 <span>↗</span></Link>
          </nav>
        </div>
      </header>
      <main>{children}</main>
      <footer className="site-footer">
        <div className="footer-main">
          <div>
            <Brand />
            <p>블로거의 반복 작업을 가볍게.<br />설치 없이, 브라우저에서 바로.</p>
          </div>
          <div className="footer-links">
            <div><strong>도구</strong><Link to="/tools/video-to-gif">동영상 GIF 변환</Link><Link to="/tools/image-resizer">이미지 리사이즈</Link><Link to="/tools/watermark">워터마크 넣기</Link></div>
            <div><strong>BlogToolTool</strong><Link to="/about">서비스 소개</Link><Link to="/guides">활용 가이드</Link><Link to="/contact">문의하기</Link></div>
            <div><strong>정책</strong><Link to="/privacy">개인정보처리방침</Link><Link to="/terms">이용약관</Link></div>
          </div>
        </div>
        <div className="footer-bottom"><span>© 2026 BlogToolTool. All rights reserved.</span><span className="local-note"><b>●</b> 파일은 기기 안에서만 처리됩니다</span></div>
      </footer>
    </div>
  );
}

function Home() {
  return (
    <>
      <Meta title="BlogToolTool — 블로거를 위한 무료 이미지·GIF 도구" description="동영상을 GIF로 변환하고, 이미지를 리사이즈하고, 워터마크를 넣으세요. 업로드 없이 브라우저에서 안전하게 처리하는 무료 블로그 도구입니다." path="/" />
      <section className="hero">
        <div className="hero-copy">
          <span className="pill"><b>NEW</b> 더 빠르고 안전하게 새로워졌어요 <span>→</span></span>
          <h1>콘텐츠는 멋지게.<br /><em>반복 작업은 가볍게.</em></h1>
          <p>블로그 이미지 편집부터 GIF 변환까지.<br />설치도, 회원가입도 없이 브라우저에서 바로 끝내세요.</p>
          <div className="hero-actions"><Link className="button primary" to="/tools">무료 도구 둘러보기 <span>↗</span></Link><a className="text-link" href="#how">어떻게 작동하나요? <span>↓</span></a></div>
          <div className="trust-row"><span><b>✓</b> 100% 무료</span><span><b>✓</b> 회원가입 없음</span><span><b>✓</b> 서버 업로드 없음</span></div>
        </div>
        <div className="hero-art" aria-label="BlogToolTool 도구 미리보기">
          <div className="orbit orbit-one" /><div className="orbit orbit-two" />
          <div className="float-chip chip-video"><span>▶</span><b>MP4</b><i>00:08</i></div>
          <div className="float-chip chip-gif"><span>GIF</span><div><b>완료!</b><i>2.4 MB</i></div><em>✓</em></div>
          <div className="float-chip chip-image"><div className="mini-landscape">●</div><b>1200 × 630</b></div>
          <div className="wand"><span>✦</span></div>
          <i className="spark s1">✦</i><i className="spark s2">✦</i><i className="spark s3">✦</i>
        </div>
      </section>

      <section className="tool-section">
        <div className="section-heading"><span className="kicker">FREE TOOLS</span><h2>필요한 작업만 골라,<br />빠르게 끝내세요</h2><p>파일은 서버로 전송되지 않습니다.<br />모든 작업은 내 브라우저에서 안전하게 처리돼요.</p></div>
        <div className="tool-grid">{tools.map((tool, index) => <ToolCard tool={tool} index={index} key={tool.path} />)}</div>
        <div className="center"><Link className="button outline" to="/tools">모든 도구 보기 <span>→</span></Link></div>
      </section>

      <section className="privacy-banner" id="how">
        <div className="shield">⌾<span>✓</span></div>
        <div><span className="kicker">PRIVACY FIRST</span><h2>내 파일은, 내 기기 안에서만.</h2><p>BlogToolTool은 파일을 서버에 업로드하지 않습니다.<br />변환과 편집은 브라우저 안에서만 진행되고, 창을 닫으면 작업 데이터도 사라져요.</p><Link to="/privacy">개인정보 보호 방식 알아보기 <span>→</span></Link></div>
        <div className="privacy-facts"><span><i>✓</i><b>서버 전송</b><em>없음</em></span><span><i>✓</i><b>파일 보관</b><em>없음</em></span><span><i>✓</i><b>가입·로그인</b><em>없음</em></span></div>
      </section>

      <section className="results-section">
        <div className="section-heading"><span className="kicker">TESTED BY US</span><h2>직접 만든 파일로<br />결과를 확인했습니다</h2><p>설명만 적지 않고 같은 원본과 구간에서 설정 하나씩 바꿔 측정했습니다.</p></div>
        <div className="result-highlights">
          <Link to="/guides/blog-image-size"><small>IMAGE RESIZE</small><strong>2.38MB → 79KB</strong><span>4160px 원본을 1200px WebP로 변환</span></Link>
          <Link to="/guides/gif-fps-guide"><small>GIF FPS</small><strong>5.91MB ↔ 11.81MB</strong><span>같은 3초 구간에서 10 FPS와 20 FPS 비교</span></Link>
          <Link to="/guides/watermark-guide"><small>WATERMARK</small><strong>모서리 35% ↔ 중앙 25%</strong><span>같은 야간 사진에서 위치와 투명도 비교</span></Link>
        </div>
      </section>

      <section className="guide-section">
        <div className="section-heading row"><div><span className="kicker">QUICK GUIDES</span><h2>작업이 쉬워지는<br />짧고 정확한 가이드</h2></div><Link to="/guides">가이드 전체 보기 <span>→</span></Link></div>
        <div className="guide-grid">{contentGuides.slice(0, 3).map((guide, index) => <GuideCard guide={guide} index={index} key={guide.slug} />)}</div>
      </section>

      <section className="bottom-cta"><span className="scribble">✦</span><h2>지금 필요한 작업,<br />바로 끝내볼까요?</h2><p>회원가입 없이 무료로 시작하세요.</p><Link className="button dark" to="/tools">무료 도구 시작하기 <span>↗</span></Link></section>
    </>
  );
}

function ToolCard({ tool, index }) {
  return (
    <Link className={`tool-card ${tool.accent}`} to={tool.path}>
      <div className={`tool-visual visual-${index}`}><span className="tool-icon">{tool.icon}</span><i className="deco">✦</i></div>
      <span className="eyebrow">{tool.eyebrow}</span><h3>{tool.title}</h3><p>{tool.description}</p><b className="card-link">바로 사용하기 <span>→</span></b>
    </Link>
  );
}

function GuideCard({ guide, index }) {
  return (
    <Link className="guide-card" to={`/guides/${guide.slug}`}>
      <div className={`guide-cover cover-${index % 3}`}><span>{guide.icon}</span><i>✦</i></div>
      <small>{guide.tag} · {guide.read}</small><h3>{guide.title}</h3><p>{guide.summary}</p><b>읽어보기 <span>→</span></b>
    </Link>
  );
}

function ToolsIndex() {
  return (
    <Page title="무료 블로그 도구" intro="콘텐츠 제작에 자주 쓰는 기능만 모았습니다. 파일은 서버에 올리지 않고 브라우저에서 처리합니다." path="/tools">
      <div className="tool-grid listing">{tools.map((tool, index) => <ToolCard tool={tool} index={index} key={tool.path} />)}</div>
    </Page>
  );
}

function GuidesIndex() {
  return (
    <Page title="블로그 작업 가이드" intro="직접 블로그와 콘텐츠를 운영하며 자주 막혔던 이미지·GIF 문제를 원인부터 해결 순서까지 정리합니다." path="/guides">
      <div className="guide-grid listing">{contentGuides.map((guide, index) => <GuideCard guide={guide} index={index} key={guide.slug} />)}</div>
    </Page>
  );
}

/* 이전의 짧은 본문 데이터는 src/content/guides.js로 교체되었습니다.
const articleBodies = {
  "blog-image-size": {
    title: "블로그 이미지, 몇 px로 올려야 선명할까?",
    lead: "이미지가 흐릿해지는 가장 흔한 이유는 원본이 너무 작거나, 플랫폼이 큰 파일을 다시 압축하기 때문입니다.",
    sections: [
      ["가장 무난한 기준", "본문 가로 이미지는 1200px을 권장합니다. 모바일에서도 충분히 선명하고, 대부분의 블로그 레이아웃에서 과도하게 크지 않습니다. 대표 이미지는 공유 카드까지 고려해 1200×630px 비율을 사용하면 편리합니다."],
      ["포맷은 어떻게 고를까?", "사진은 JPG 또는 WebP, 투명 배경이 필요한 로고와 그래픽은 PNG가 적합합니다. WebP는 같은 체감 화질에서 용량을 줄이기 좋지만, 원본 보관용 파일은 별도로 남겨 두세요."],
      ["업로드 전 체크리스트", "긴 변은 1200~1600px 사이로 맞추고, 사진 품질은 80~88%에서 시작하세요. 파일명은 IMG_001보다 cafe-interior-seoul처럼 내용을 설명하는 단어가 낫고, 대체 텍스트에는 실제 이미지 내용을 자연스럽게 적는 것이 좋습니다."],
    ],
  },
  "gif-optimization": {
    title: "화질은 지키고 GIF 용량은 줄이는 5가지 방법",
    lead: "GIF는 짧고 눈에 잘 띄지만 색상과 프레임이 쌓일수록 용량이 빠르게 커집니다. 아래 순서로 줄이면 품질 손실을 최소화할 수 있습니다.",
    sections: [
      ["1. 가장 먼저 길이를 줄이세요", "필요한 장면 앞뒤의 정지 구간부터 덜어내세요. 8초 영상을 5초로 줄이면 다른 설정을 건드리지 않아도 용량이 크게 감소합니다."],
      ["2. FPS는 10~15부터", "일반적인 사용법 안내나 화면 녹화는 12 FPS 전후로도 충분히 자연스럽습니다. 빠른 움직임이 있을 때만 15~20 FPS를 사용하세요."],
      ["3. 본문 너비에 맞추세요", "블로그 본문에서 700px로 보이는 GIF를 1920px로 만들 필요는 없습니다. 실제 표시 크기의 1~1.5배 정도면 충분합니다."],
      ["4. 한 GIF에는 한 동작만", "여러 설명을 하나의 긴 GIF에 담기보다 작업 단위로 나누면 읽는 사람도 이해하기 쉽고 페이지 로딩도 빨라집니다."],
    ],
  },
  "watermark-guide": {
    title: "사진을 해치지 않는 워터마크 위치와 투명도",
    lead: "좋은 워터마크는 눈에 거슬리지 않으면서도 이미지만 잘라 재사용하기 어렵게 만듭니다.",
    sections: [
      ["투명도는 25~45%", "흰색 또는 검은색 문자를 사용하고 사진 밝기에 맞춰 투명도를 조정하세요. 너무 옅으면 보호 효과가 없고, 너무 진하면 콘텐츠 감상을 방해합니다."],
      ["모서리에서 조금 띄우기", "가장자리에서 이미지 너비의 2~4% 정도 안쪽에 배치하면 안정적입니다. 다만 쉽게 잘라낼 수 없는 위치가 필요하다면 피사체를 피한 중앙 주변에 작은 워터마크를 두는 방법도 있습니다."],
      ["채널명을 일관되게", "블로그 주소, 활동명, 저작권 표기 중 하나를 정해 모든 이미지에 같은 글꼴과 크기로 사용하면 브랜드 인지도에도 도움이 됩니다."],
    ],
  },
};
*/

function GuideArticle() {
  const slug = useLocation().pathname.split("/").filter(Boolean).pop();
  const article = guideMap[slug];
  if (!article) return <NotFound />;
  const relatedGuides = contentGuides.filter((guide) => guide.slug !== slug).slice(0, 3);
  return (
    <article className="article">
      <Meta title={`${article.title} | BlogToolTool`} description={article.summary} path={`/guides/${slug}`} type="article" published={article.published} modified={article.updated} images={article.experiment?.images.map((item) => item.src)} />
      <Link className="back-link" to="/guides">← 가이드 목록</Link>
      <span className="kicker">BLOGTOOLTOOL GUIDE</span>
      <h1>{article.title}</h1>
      <div className="article-meta"><span>제티뷰</span><time dateTime={article.updated}>{article.updated} 업데이트</time><span>{article.read}</span></div>
      <p className="article-lead">{article.lead}</p>
      <aside className="article-summary"><b>이 글에서 해결할 내용</b><p>{article.summary}</p></aside>
      {article.experiment && <ExperimentEvidence experiment={article.experiment} />}
      <div className="article-body">
        {article.sections.map((section) => (
          <section key={section.title}>
            <h2>{section.title}</h2>
            {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            {section.bullets && <ul>{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>}
            {section.table && <div className="article-table-wrap"><table><thead><tr>{section.table.headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{section.table.rows.map((row) => <tr key={row.join("-")}>{row.map((cell) => <td key={cell}>{cell}</td>)}</tr>)}</tbody></table></div>}
          </section>
        ))}
      </div>
      <section className="article-faq"><span className="kicker">FAQ</span><h2>자주 묻는 질문</h2>{article.faq.map((item) => <details key={item.q}><summary>{item.q}</summary><p>{item.a}</p></details>)}</section>
      <div className="article-tool"><div><b>{article.toolLabel}</b><p>파일을 서버에 올리지 않고 브라우저에서 처리합니다.</p></div><Link className="button primary" to={article.toolPath}>무료 도구 열기 →</Link></div>
      <section className="article-author"><b>작성자 제티뷰</b><p>네이버 블로그와 유튜브를 운영하며 게임 공략, 제품 리뷰, 일상의 시행착오를 기록해 왔습니다. BlogToolTool에서는 실제 게시 작업에서 반복되는 이미지·GIF 문제를 직접 해결할 수 있게 정리합니다.</p><a href="https://blog.naver.com/jhonbeetbox" target="_blank" rel="noreferrer">네이버 블로그 보기 ↗</a></section>
      <section className="related-guides"><h2>함께 읽기</h2><div>{relatedGuides.map((guide) => <Link key={guide.slug} to={`/guides/${guide.slug}`}><small>{guide.tag}</small><b>{guide.title}</b></Link>)}</div></section>
    </article>
  );
}

function ExperimentEvidence({ experiment }) {
  return (
    <section className="experiment-evidence" aria-labelledby="experiment-title">
      <span className="kicker">DIRECT TEST</span>
      <h2 id="experiment-title">{experiment.title}</h2>
      <p>{experiment.description}</p>
      <div className={`evidence-gallery ${experiment.images.length === 1 ? "single" : ""}`}>
        {experiment.images.map((item) => (
          <figure key={item.src}>
            <a href={item.src} target="_blank" rel="noreferrer">
              <img src={item.src} alt={item.alt} width={item.width} height={item.height} loading="lazy" />
            </a>
            <figcaption>{item.caption}</figcaption>
          </figure>
        ))}
      </div>
      {experiment.table && <div className="article-table-wrap"><table><thead><tr>{experiment.table.headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{experiment.table.rows.map((row) => <tr key={row.join("-")}>{row.map((cell) => <td key={cell}>{cell}</td>)}</tr>)}</tbody></table></div>}
      <p className="evidence-note">{experiment.note}</p>
    </section>
  );
}

function Page({ title, intro, path, children }) {
  return <section className="subpage"><Meta title={`${title} | BlogToolTool`} description={intro} path={path} /><span className="kicker">BLOGTOOLTOOL</span><h1>{title}</h1><p className="subpage-intro">{intro}</p>{children}</section>;
}

function About() {
  return <Page title="블로거의 귀찮은 작업을 줄입니다" intro="BlogToolTool은 직접 블로그와 유튜브 콘텐츠를 만들며 반복했던 이미지·영상 작업을 더 간단하게 해결하려고 만든 개인 프로젝트입니다." path="/about"><div className="prose"><h2>운영자 제티뷰</h2><p>게임 공략과 제품 리뷰, 일상 기록을 네이버 블로그에 써 왔고 콘솔 게임 중심의 유튜브 채널도 운영하고 있습니다. 글 한 편을 올릴 때마다 사진 크기를 맞추고, 짧은 장면을 GIF로 만들고, 출처를 표시하는 작업이 반복됐습니다. BlogToolTool은 그 과정을 줄이기 위해 직접 만든 도구입니다.</p><p><a href="https://blog.naver.com/jhonbeetbox" target="_blank" rel="noreferrer">제티뷰 네이버 블로그 ↗</a></p><h2>작지만 제대로 작동하는 도구</h2><p>거대한 편집 프로그램을 켜지 않고도 블로그에 사진 한 장을 올리는 데 필요한 일을 빠르게 끝낼 수 있어야 한다고 생각합니다. 기능은 이해하기 쉽게, 결과물은 바로 확인할 수 있게 만들고 있습니다.</p><h2>가이드는 이렇게 작성합니다</h2><p>검색어를 채우기 위한 글보다 실제 작업 중 생기는 문제와 해결 순서를 중심으로 씁니다. 확인하지 않은 수치나 결과를 경험한 것처럼 적지 않으며, 서비스별 사양처럼 바뀔 수 있는 정보는 특정 숫자를 정답처럼 단정하지 않습니다. 글의 오류를 발견하면 수정일을 표시해 고칩니다.</p><h2>개인정보 보호가 기본입니다</h2><p>현재 제공하는 편집 도구는 가능한 모든 처리를 사용자의 브라우저 안에서 수행합니다. 선택한 파일을 BlogToolTool 서버에 업로드하거나 보관하지 않습니다.</p><h2>오류와 제안</h2><p>도구 오류, 글의 잘못된 정보, 필요한 기능은 jhonbeetbox@naver.com으로 알려주세요. 실제 블로그 작업에서 자주 반복되는 문제부터 확인하고 개선하겠습니다.</p></div></Page>;
}

function Privacy() {
  return <Page title="개인정보처리방침" intro="시행일: 2026년 8월 30일" path="/privacy"><div className="prose policy"><h2>1. 도구에서 선택한 파일</h2><p>BlogToolTool은 회원가입을 요구하지 않습니다. 이미지와 영상 변환은 사용자의 브라우저 안에서 처리되며, 선택한 파일이나 완성 파일을 BlogToolTool 서버로 업로드하거나 저장하지 않습니다. 페이지를 닫으면 브라우저 메모리에 있던 작업 데이터도 사라집니다.</p><h2>2. 접속 과정에서 처리될 수 있는 정보</h2><p>사이트 제공과 보안을 위해 호스팅 사업자가 IP 주소, 브라우저 종류, 방문 시각, 요청한 페이지 같은 접속 기록을 일시적으로 처리할 수 있습니다. BlogToolTool은 사용자가 도구에서 선택한 파일의 내용을 이러한 접속 기록과 결합하지 않습니다.</p><h2>3. Google AdSense와 쿠키</h2><p>이 사이트는 광고 제공을 위해 Google AdSense를 사용하거나 사용할 수 있습니다. Google과 그 파트너는 쿠키, 웹 비콘, IP 주소 또는 기타 식별자를 사용해 광고를 제공하고, 광고 성과를 측정하며, 부정 사용을 방지할 수 있습니다. 사용자의 설정과 적용 법률에 따라 관심 기반 광고가 표시될 수 있습니다.</p><p>Google의 광고 쿠키 사용 방식은 <a href="https://policies.google.com/technologies/ads?hl=ko" target="_blank" rel="noreferrer">Google 광고 개인정보 보호 안내 ↗</a>에서 확인할 수 있으며, <a href="https://adssettings.google.com/" target="_blank" rel="noreferrer">Google 광고 설정 ↗</a>에서 맞춤 광고를 관리할 수 있습니다.</p><h2>4. 동의와 쿠키 관리</h2><p>동의가 필요한 지역의 방문자에게는 Google이 요구하는 동의 관리 절차가 제공될 수 있습니다. 사용자는 브라우저 설정에서 쿠키를 차단하거나 삭제할 수 있지만, 이 경우 일부 분석 또는 광고 기능이 정상 작동하지 않을 수 있습니다.</p><h2>5. 외부 링크</h2><p>사이트에는 운영자의 네이버 블로그 등 외부 사이트로 이동하는 링크가 있습니다. 외부 사이트의 개인정보 처리는 해당 서비스의 정책을 따릅니다.</p><h2>6. 보유 기간과 문의</h2><p>BlogToolTool은 도구 사용 파일을 수집하거나 보관하지 않습니다. 개인정보 및 사이트 운영 관련 문의는 <a href="mailto:jhonbeetbox@naver.com">jhonbeetbox@naver.com</a>으로 보내주세요.</p><h2>7. 정책 변경</h2><p>서비스나 관련 법령이 변경되면 이 방침을 수정할 수 있으며, 변경된 시행일을 이 페이지 상단에 표시합니다.</p></div></Page>;
}

function Terms() {
  return <Page title="이용약관" intro="시행일: 2026년 7월 28일" path="/terms"><div className="prose policy"><h2>1. 서비스 이용</h2><p>BlogToolTool은 별도 고지 없이 기능을 개선·변경할 수 있습니다. 사용자는 관계 법령과 타인의 저작권을 준수하는 범위에서 도구를 이용해야 합니다.</p><h2>2. 결과물과 책임</h2><p>변환 전 원본 파일은 사용자가 직접 안전하게 보관해야 합니다. 브라우저, 기기 성능, 파일 형식에 따라 결과가 달라질 수 있으므로 중요한 작업은 결과물을 확인한 후 사용해 주세요.</p><h2>3. 금지 행위</h2><p>서비스 운영을 방해하거나, 악성 코드를 유포하거나, 타인의 권리를 침해하는 목적으로 서비스를 이용할 수 없습니다.</p><h2>4. 문의</h2><p>서비스 관련 문의: jhonbeetbox@naver.com</p></div></Page>;
}

function Contact() {
  return <Page title="문의하기" intro="도구 오류, 콘텐츠 수정 요청, 개인정보 및 광고 관련 문의를 받습니다." path="/contact"><div className="prose"><h2>연락처</h2><p>이메일: <a href="mailto:jhonbeetbox@naver.com">jhonbeetbox@naver.com</a></p><p>문의 내용과 문제가 발생한 페이지 주소, 사용한 브라우저를 함께 적어주시면 확인하기 쉽습니다. 파일에 개인정보가 포함되어 있다면 원본 대신 문제가 보이는 화면만 보내주세요.</p><h2>문의 가능한 내용</h2><p>이미지·GIF·워터마크 도구 오류, 가이드의 잘못된 정보, 저작권 또는 개인정보 관련 요청, 광고 및 서비스 운영 관련 문의를 확인합니다.</p><h2>운영자</h2><p>BlogToolTool은 블로그와 유튜브를 운영하는 제티뷰가 직접 만들고 관리하는 개인 프로젝트입니다. 서비스 배경과 콘텐츠 작성 원칙은 <Link to="/about">서비스 소개</Link>에서 확인할 수 있습니다.</p></div></Page>;
}

function NotFound() {
  return <section className="not-found"><Meta title="페이지를 찾을 수 없습니다 | BlogToolTool" description="요청한 페이지를 찾을 수 없습니다." path="/404" noindex /><b>404</b><h1>길을 잃으셨나요?</h1><p>요청한 페이지가 없거나 주소가 변경되었습니다.</p><Link className="button primary" to="/">홈으로 돌아가기</Link></section>;
}

function ToolLayout({ children }) {
  return <div className="tool-page">{children}</div>;
}

function App() {
  const Router = window.location.hostname.endsWith("github.io") ? HashRouter : BrowserRouter;
  return (
    <Router basename={Router === BrowserRouter ? process.env.PUBLIC_URL : undefined}>
      <ScrollToTop />
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/tools" element={<ToolsIndex />} />
          <Route path="/tools/video-to-gif" element={<ToolLayout><GIFConverter /></ToolLayout>} />
          <Route path="/gifconverter" element={<ToolLayout><GIFConverter /></ToolLayout>} />
          <Route path="/tools/image-resizer" element={<ToolLayout><ImageController /></ToolLayout>} />
          <Route path="/tools/watermark" element={<ToolLayout><Watermark /></ToolLayout>} />
          <Route path="/watermark" element={<ToolLayout><Watermark /></ToolLayout>} />
          <Route path="/guides" element={<GuidesIndex />} />
          <Route path="/guides/:slug" element={<GuideArticle />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </Router>
  );
}

export default App;
